/**
 * Giving an enrolled machine the account key. Only this trusted browser can do it: the machine's
 * relayed content identity must verify against the enrollment pin this browser created (from its
 * own passkey-approved enrollment). The addition is then signed by this browser's endorsed device
 * key with no further tap (docs/e2e.md "Fewer passkey ceremonies"); only a fresh account without a
 * directory, or a pending revocation rotation, needs a passkey. Detection never prompts.
 */
import { type Api, ApiError } from './api.js'
import {
  type BrowserContentSession,
  GrantNeedsPasskey,
  type MachineAccess,
} from './content-session.js'

export type { MachineAccess }

/** Add `machineId` as a key recipient (provisioning the directory if needed; may prompt). */
export async function grantMachineAccess(content: BrowserContentSession, machineId: string) {
  await content.currentKey(machineId)
}

/**
 * Zero-tap grant: true once granted, false when only a passkey ceremony could do it (fresh
 * account or pending rotation). Never opens a passkey sheet.
 */
export async function grantMachineAccessSilently(
  content: BrowserContentSession,
  machineId: string,
): Promise<boolean> {
  try {
    await content.currentKey(machineId, { passkey: false })
    return true
  } catch (e) {
    if (e instanceof GrantNeedsPasskey) return false
    throw e
  }
}

/** Poll the relay until the machine's content identity exists (vikod connected once). */
export async function waitForMachineIdentity(
  api: Pick<Api, 'readContentIdentity'>,
  machineId: string,
  o: { timeoutMs: number; intervalMs?: number; signal?: AbortSignal },
): Promise<boolean> {
  const until = Date.now() + o.timeoutMs
  for (;;) {
    if (o.signal?.aborted) return false
    try {
      await api.readContentIdentity(machineId)
      return true
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'content_identity_pending')) throw e
    }
    if (Date.now() + (o.intervalMs ?? 1_500) > until) return false
    await new Promise((r) => setTimeout(r, o.intervalMs ?? 1_500))
  }
}

export const machineAccessKey = (machineId: string) => ['machines', 'access', machineId] as const
